import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'

import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Youtube from '@tiptap/extension-youtube'

import Compress from 'compress.js'

import swal from 'sweetalert'
const mime = require('mime')

export default class extends Controller {
  static values = {
    cdnDomain: String
  }

  static targets = [
    'editor',
    'textInput',
    'imageInput',
    'submit'
  ]

  connect () {
    this.initTiptap()
    this.initScrollableObserver()
  }

  disconnect() {
    this.editor.destroy()
    this.observer.disconnect()
  }

  initScrollableObserver() {
    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.dataset.userId == document.body.dataset.currentUserId) {
              node.classList.add('from-me')
            } else {
              node.classList.remove('from-me')
            }

            this.setScrollableTop()
          }
        })
      })
    })

    this.observer.observe(this.scrollable, { childList: true, subtree: true });
  }

  setScrollableTop() {
    this.scrollable.scrollTop = this.scrollable.scrollHeight
  }

  initTiptap() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          orderedList: false,
          bulletList: false
        }),
        Placeholder,
        Image,
        Link.extend({
          inclusive: false,
          exitable: true,
        }),
        Typography,
        Youtube,
      ],
      editorProps: {
        transformPastedHTML(html) {
          var div = document.createElement('div')
          div.innerHTML = html
          return div.textContent || div.innerText || ''
        },
      },
    })

    this.editor.on('update', ({ editor }) => {
      this.textInputTarget.value = editor.getHTML()
      this.setScrollableTop()
    })

    this.editor.on('create', ({ _e }) => {
      this.setScrollableTop()
    })
  }

  processImagePasteEvent(event) {
    const items = (event.clipboardData || event.originalEvent.clipboardData).items

    for (const item of items) {
      const allowed = ['image/gif', 'image/jpeg', 'image/png', 'image/webp']

      if (item.kind === "file" && allowed.includes(item.type)) {
        let tmp_file = item.getAsFile()

        tmp_file.arrayBuffer().then(buffer => {
          let file = new File([buffer], `pasted_${Date.now()}.${mime.getExtension(tmp_file.type)}`)
          this.compressAndUploadImage(file)
        }).catch(e => console.log(e))

        break
      }
    }
  }

  openAttachImage() {
    this.imageInputTarget.click()
  }

  submit() {
    this.submitTarget.click()
  }

  processImageInputChange() {
    const file = this.imageInputTarget.files[0]

    // Ensure only images are uploaded
    if (/^image\//.test(file.type)) {
      this.compressAndUploadImage(file)
    } else {
      swal('Only images, please')
    }
  }

  compressAndUploadImage(file) {
    const compress = new Compress()

    compress.compress([file], {
      size: 1, // the max size in MB, defaults to 2MB
      quality: .8, // the quality of the image, max is 1,
      maxWidth: 1200, // the max width of the output image, defaults to 1920px
      maxHeight: 1200, // the max height of the output image, defaults to 1920px
      resize: true, // defaults to true, set false if you do not want to resize the image width and height
      rotate: false, // See the rotation section below
    }).then((data) => {
      const base64str = data[0].data
      const imgExt = data[0].ext
      const blob = Compress.convertBase64ToFile(base64str, imgExt)
      const compressed = new File([blob], `image_${Date.now()}.${mime.getExtension(file.type)}`)

      this.uploadImage(compressed)
    })
  }

  uploadImage(file) {
    const formData = new FormData()
    formData.append('file', file)

    fetch('/uploads', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(blob => {
      this.insertImage(
        `https://${this.cdnDomainValue}/${blob.key}`
      );
    })
    .catch(error => {
      console.log(error);
    })
  }

  insertImage(fileUrl) {
    if (this.editor && !this.editor.isDestroyed) {
      this.editor.chain().focus().setImage({ src: fileUrl }).run()
    }
  }

  get scrollable() {
    return document.querySelector('turbo-frame.scrollable')
  }
}
